// openAIUtils.js
import Swal from 'sweetalert2';
import '@sweetalert2/theme-dark';
import { setPromptSource } from '../store/slices/truPromptSlice';
import OpenAI from "openai";

// Function to update OpenAI results and store them in local storage
export const updateOpenAIResults = (setOpenAIResults, newResults) => {
    setOpenAIResults(prevResults => {
        const currentResults = Array.isArray(prevResults) ? prevResults : [];

        const updatedResults = [
            ...currentResults,
            ...newResults.map(result => ({
                ...result,
                savedStatus: false,
                promptDBId: "",
                language: result.language || 'EN', // Include language, default to 'EN'
                expiration: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
            }))
        ];

        // Store in local storage with language information
        const dataToStore = {
            results: updatedResults
        };
        localStorage.setItem('openAIResults', JSON.stringify(dataToStore));

        return updatedResults;
    });
};

// Function to handle prompt search
export const handlePromptSearch = async (
    e,
    prompt,
    setPrompt,
    setLoading,
    setOpenAIResults,
    selectedOptions,
    setSelectedOptions,
    dispatch,
    selectedLanguage
) => {
    e.preventDefault();

    // Initialize OpenAI with your API key and allow browser usage
    const openai = new OpenAI({
        apiKey: process.env.REACT_APP_OPENAI_API, // Ensure your API key is set correctly
        dangerouslyAllowBrowser: true, // Allow usage in a browser-like environment
    });

    const optionsList = [
        { name: "SEO", param: "As an SEO expert, focus on tasks like on-page optimization (meta tags, content structure), off-page strategies (backlink building, outreach), and performing a comprehensive site audit to identify issues and opportunities" },
        { name: "Design", param: "As a designer, consider aspects such as user experience, visual aesthetics, brand consistency, and effective communication through design elements" },
        { name: "Communication", param: "As a communication specialist, focus on effective messaging, audience engagement, and strategies for clear and impactful communication across various channels" },
        { name: "Web Development", param: "As a web developer, consider aspects such as responsive design, performance optimization, accessibility, and modern web technologies" },
        { name: "Lead Generation", param: "As a lead generation expert, focus on strategies to attract and convert potential customers into qualified leads for the business" },
        { name: "Lifecycle Marketing", param: "As a lifecycle marketing specialist, focus on strategies for customer acquisition, retention, and growth across various stages of the customer journey" },
        { name: "Business Development", param: "As a business development professional, focus on strategies for growth, partnerships, market expansion, and improving overall business performance" },
    ];

    if (prompt) {
        // Convert language code to full name for API instruction
        const languageFullName = selectedLanguage === 'HN' ? 'Hindi' : 'English';
        
        const languageInstruction = languageFullName === 'Hindi'
            ? `Respond in Hindi language. Use proper Hindi grammar and vocabulary. Make sure the response is natural and fluent in Hindi. If the input contains English words, keep them as is but respond in Hindi.`
            : 'Respond in English language.';

        const searchPrompt = `Generate only a powerful and descriptive prompt for the following scenario: Write A Prompt For ${prompt}. Do not provide a solution, only a well-crafted prompt that can guide someone else to create a response. ${languageInstruction}`.trim();

        setLoading(true);

        // Ensure autoSelectedOptions is an array
        let autoSelectedOptions = Array.isArray(selectedOptions) && selectedOptions.length > 0 ? selectedOptions : ["SEO"];

        // Combine selected options into a single string
        const autoSelectedOptionsString = selectedOptions.join(', ');

        const handleError = (error) => {
            let errorMessage = 'An unexpected error occurred. Please try again later.';
            const errorCode = error.response?.status || error.code || null;
            // console.log('err:', error);

            if (errorCode === 429) {
                errorMessage = 'You have exceeded your current quota. Please check your plan and billing details.';
            } else if (errorCode === 401) {
                errorMessage = 'Unauthorized access. Please verify your API key or login credentials.';
            } else if (errorCode === 500) {
                errorMessage = 'A server error occurred. Please try again later.';
            } else if (errorCode === 400) {
                errorMessage = 'Bad request. Please check your input and try again.';
            }

            Swal.fire({
                title: 'Error!',
                text: errorMessage,
                icon: 'error',
                confirmButtonText: 'OK',
                theme: 'dark'
            });
        };

        try {
            // Combine selected options into a single prompt
            const combinedOptions = autoSelectedOptions.map(optionName => {
                const option = optionsList.find(opt => opt.name === optionName.trim());
                return option ? option.param : '';
            }).join(' and ');

            const messages = [
                {
                    role: "system",
                    content: `As a multi-expert, consider the following aspects: ${combinedOptions}. Your task is to generate a prompt ONLY, which will guide the next steps. Do not provide solutions or answers. THE RESPONSE MUST FOLLOW THIS FORMAT:

                    Optimizer: [- Provide multiple key context points or goals as bullet points.]
                    |
                    Prompt: [Generate a well-crafted and detailed prompt that is impactful]. ${languageFullName === 'Hindi' ? 'Ensure the response is in proper Hindi with correct grammar and natural flow.' : ''}`
                },
                {
                    role: "user",
                    content: searchPrompt
                }
            ];

            // Make API call to OpenAI
            const stream = await openai.chat.completions.create({
                model: "gpt-4o-mini",
                messages: messages,
                temperature: 0.7,
            });

            const content = stream.choices[0]?.message?.content || "";
            const [optimizer, generatedPrompt] = content.split('|').map(part => part ? part.replace(/(Optimizer: |Prompt: )/, '').trim() : "");

            const searchId = Date.now();

            const structuredResponse = {
                category: autoSelectedOptionsString,
                userPrompt: prompt,
                optimizer: optimizer,
                prompt: generatedPrompt,
                id: searchId,
                language: selectedLanguage, // Store the language code ('EN' or 'HN')
                expiration: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
                promptDBId: "",
                savedStatus: false
            };

            // Update the OpenAI results using the utility function
            updateOpenAIResults(setOpenAIResults, [structuredResponse]);

            // Reset selectedOptions and prompt to empty
            setSelectedOptions([]);
            setPrompt("");

            // Dispatch an action (if applicable)
            dispatch(setPromptSource('openAIPrompts'));
            // dispatch(setPromptSource('singlePrompt'));
        } catch (error) {
            console.error('Error fetching from OpenAI API:', error);
            handleError(error)
        } finally {
            setLoading(false);
        }
    } else {
        Swal.fire({
            title: 'Error!',
            text: 'Please provide the prompt...',
            icon: 'error',
            confirmButtonText: 'OK',
            theme: 'dark'
        });
    }
};
